/**
 * Task keys (commit ↔ task links, slice 1): a short, stable, human handle per
 * task — the board's prefix and the task's number, `APB-42`, as in Linear and
 * Jira. Pure; numbers are planned in taskNumbers.js.
 *
 * - prefix: 2–5 characters A–Z / 0–9, starting with a letter;
 * - key: `${prefix}-${number}`, shown upper case, matched case-insensitively;
 * - branch name: `apb-42-short-title` (Linear's format).
 */

export const PREFIX_RE = /^[A-Z][A-Z0-9]{1,4}$/;
export const FALLBACK_PREFIX = 'TSK';
const MAX_BRANCH_TITLE = 40;

/** "Überblick Straße" → "UBERBLICK STRASSE": ASCII letters/digits only, upper case. */
export function asciiFold(text) {
  return String(text || '')
    .replace(/ß/g, 'ss')
    .replace(/Æ/g, 'AE')
    .replace(/æ/g, 'ae')
    .replace(/Ø/g, 'O')
    .replace(/ø/g, 'o')
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '');
}

/** A stored prefix if it is a valid one (normalised to upper case), else null. */
export function validPrefix(prefix) {
  const p = String(prefix || '').trim().toUpperCase();
  return PREFIX_RE.test(p) ? p : null;
}

/**
 * A board's prefix from its name, not one of `taken` (prefixes of the other
 * boards the user can see): ≥ 2 words → initials (max 4), one word → its first
 * three characters, too short → TSK; taken → append 2, 3, …
 */
export function derivePrefix(name, taken = []) {
  const words = asciiFold(name)
    .toUpperCase()
    .split(/[^A-Z0-9]+/)
    .filter(Boolean);
  // A prefix starts with a letter: leading digits of the first word are skipped.
  const lettered = words.map((w, i) => (i === 0 ? w.replace(/^[0-9]+/, '') : w)).filter(Boolean);
  let base = '';
  if (lettered.length >= 2) base = lettered.slice(0, 4).map((w) => w[0]).join('');
  else if (lettered.length === 1) base = lettered[0].slice(0, 3);
  if (!/^[A-Z]/.test(base) || base.length < 2) base = FALLBACK_PREFIX;
  const used = new Set((taken || []).map((p) => String(p || '').toUpperCase()));
  if (!used.has(base)) return base;
  const stem = base.slice(0, 4);
  for (let n = 2; n < 100; n += 1) {
    const candidate = `${stem.slice(0, 5 - String(n).length)}${n}`;
    if (!used.has(candidate)) return candidate;
  }
  return base;
}

/** The stored prefixes of every board in `summaries` except `boardId` (prefix uniqueness). */
export function otherBoardPrefixes(summaries, boardId) {
  const out = [];
  for (const b of summaries || []) {
    if (!b || String(b.id) === String(boardId)) continue;
    const p = validPrefix(b.keyPrefix);
    if (p) out.push(p);
  }
  return out;
}

/** `APB-42`, or null without a valid prefix and a positive integer number. */
export function formatTaskKey(prefix, number) {
  const p = validPrefix(prefix);
  const n = Number(number);
  if (!p || !Number.isInteger(n) || n < 1) return null;
  return `${p}-${n}`;
}

/** "APB-42" / "apb-42" → { prefix: 'APB', number: 42 }, else null. */
export function parseTaskKey(key) {
  const m = /^([A-Za-z][A-Za-z0-9]{1,4})-([1-9][0-9]{0,8})$/.exec(String(key || '').trim());
  return m ? { prefix: m[1].toUpperCase(), number: Number(m[2]) } : null;
}

/** "Fix the Login-Page (Safari)!" → "fix-the-login-page-safari", capped at 40 characters on a word edge. */
export function titleSlug(title, max = MAX_BRANCH_TITLE) {
  const slug = asciiFold(title)
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
  if (slug.length <= max) return slug;
  const cut = slug.slice(0, max);
  const edge = cut.lastIndexOf('-');
  return (edge > max / 2 ? cut.slice(0, edge) : cut).replace(/-+$/g, '');
}

/** `apb-42-short-title` (or `apb-42` for an untitled task); null without a key. */
export function branchNameFor(key, title) {
  const parsed = parseTaskKey(key);
  if (!parsed) return null;
  const head = `${parsed.prefix.toLowerCase()}-${parsed.number}`;
  const slug = titleSlug(title);
  return slug ? `${head}-${slug}` : head;
}
